import type { ReactNode } from 'react';

import { formatRelativeOrAbsolute, toMachineDate } from '@shared/format';

import { cn } from './cn';

/**
 * The design system's comment anatomy — a face, a head carrying the author and
 * the time, a body, and an optional action row.
 *
 * **One component, three surfaces.** A case thread is this anatomy with the
 * party on `data-from`; a report thread is the same; a held comment in the
 * moderation queue is the same with a badge in its head. None of them is a new
 * message class, and the design system says so directly: a case thread is «the
 * comment component with a party attribute».
 *
 * **The party is `operator` on both hosts, never `us` / `them`.** The side is a
 * property of the message rather than of who is reading it, and the owning
 * service stores it on the row precisely so no surface has to derive it from a
 * role lookup that may have changed since the message was written.
 *
 * Promoted to the shared ring on its third surface. Colour never carries the
 * side alone — the author's name is written in every head.
 */
export function CommentFace() {
  return (
    <span
      aria-hidden="true"
      className="size-6 shrink-0 rounded-full border border-border [background:linear-gradient(135deg,color-mix(in_srgb,var(--brand-noir)_78%,var(--brand-bone)_22%),color-mix(in_srgb,var(--brand-noir)_92%,var(--brand-bone)_8%))]"
    />
  );
}

export function Comment({
  author,
  timestamp,
  body,
  fromOperator = false,
  reply = false,
  nested = false,
  badge,
  actions,
}: {
  author: string;
  /** ISO-8601 on the wire; relative inside a week and absolute after it. */
  timestamp: string;
  body: string;
  fromOperator?: boolean;
  reply?: boolean;
  /**
   * True when the comment already sits inside an `<article>` — the moderation
   * queue wraps each held comment in a work-item card, and an `<article>` inside
   * an `<article>` announces two articles per item. In a thread it stands alone
   * and takes the element itself.
   */
  nested?: boolean;
  badge?: ReactNode;
  actions?: ReactNode;
}) {
  const Root = nested ? 'div' : 'article';

  return (
    <Root
      data-from={fromOperator ? 'operator' : undefined}
      className={cn(
        'flex gap-3 py-4',
        reply && 'ms-7 border-s-2 border-border ps-4',
        fromOperator && 'rounded-md bg-bg-sunken px-4',
      )}
    >
      <CommentFace />

      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-sm font-semibold text-ink">{author}</span>
          {badge}
          <span className="text-xs text-ink-muted">
            {/*
              The machine value rides the same element as the human string, and
              every comparison anywhere reads the attribute rather than the
              rendered «قبل يومين».
            */}
            <time dateTime={toMachineDate(timestamp)}>{formatRelativeOrAbsolute(timestamp)}</time>
          </span>
        </div>

        <p className="mb-0 max-w-measure whitespace-pre-line text-sm text-ink">{body}</p>

        {actions ? <div className="mt-3 flex flex-wrap gap-2">{actions}</div> : null}
      </div>
    </Root>
  );
}
